import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { PermissionsProvider } from "./lib/permissions";
import { MainLayout } from "./components/layout/MainLayout";
import { RoleGuard } from "./components/RoleGuard";
import { RequireModule } from "./lib/featureFlags";
import { Toaster } from "./components/ui/sonner";

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[400px]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);



// Critical pages - load immediately
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import RequestAccessPage from "./pages/RequestAccessPage";

// Lazy-loaded pages for better performance
const CasesPage = lazy(() => import("./pages/CasesPage"));
const CaseDetailPage = lazy(() => import("./pages/CaseDetailPage"));
const ApprovalsQueuePage = lazy(() => import("./pages/ApprovalsQueuePage"));
const IntakePage = lazy(() => import("./pages/IntakePage"));
const ReportsPage = lazy(() => import("./pages/ReportsPage"));
const EUReturnsPage = lazy(() => import("./pages/EUReturnsPage"));
const USAReturnsPage = lazy(() => import("./pages/USAReturnsPage"));
const EUH7CompilationPage = lazy(() => import("./pages/EUH7CompilationPage"));
const EUH7ReasonMapperPage = lazy(() => import("./pages/EUH7ReasonMapperPage"));
const DataQualityPage = lazy(() => import("./pages/DataQualityPage"));
const AuditLogsPage = lazy(() => import("./pages/AuditLogsPage"));
const OverviewPage = lazy(() => import("./pages/OverviewPage"));
const InsightsPage = lazy(() => import("./pages/InsightsPage"));
const UsersPage = lazy(() => import("./pages/UsersPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const BillingSettingsPage = lazy(() => import("./pages/BillingSettingsPage"));
const BillingRequestsPage = lazy(() => import("./pages/BillingRequestsPage"));
const IntegrationsSettingsPage = lazy(() => import("./pages/IntegrationsSettingsPage"));
const TrialOnboardingPage = lazy(() => import("./pages/TrialOnboardingPage"));
const AdminAnalyticsPage = lazy(() => import("./pages/AdminAnalyticsPage"));
const ProspectsCockpitPage = lazy(() => import("./pages/ProspectsCockpitPage"));
const FeatureFlagsPage = lazy(() => import("./pages/FeatureFlagsPage"));
const BulkInvitePage = lazy(() => import("./pages/BulkInvitePage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const SandboxInviteRedeemPage = lazy(() => import("./pages/SandboxInviteRedeemPage"));
const InviteRedeemPage = lazy(() => import("./pages/InviteRedeemPage"));
const BrokerClientDetailPage = lazy(() => import("./pages/BrokerClientDetailPage"));
const BrokerCaseDetailPage = lazy(() => import("./pages/BrokerCaseDetailPage"));
const SystemAdminPage = lazy(() => import("./pages/SystemAdminPage"));
const CDSPlatformPage = lazy(() => import("./pages/admin/CDSPlatformPage"));
const HMRCNotificationsViewerPage = lazy(() => import("./pages/admin/HMRCNotificationsViewerPage"));
const WorkQueuePage = lazy(() => import("./pages/WorkQueuePage"));
const ApproverQueuePage = lazy(() => import("./pages/ApproverQueuePage"));
// Iter258 — IntegrationsPage retired. `/integrations` redirects to `/connectors`.
const ConnectorManagementPage = lazy(() => import("./pages/ConnectorManagementPage"));
const SyncDataViewerPage = lazy(() => import("./pages/SyncDataViewerPage"));
const MFASettingsPage = lazy(() => import("./pages/MFASettingsPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));
const SampleDataPage = lazy(() => import("./pages/SampleDataPage"));
const ClaimPackBuilderPage = lazy(() => import("./pages/ClaimPackBuilderPage"));
const ClaimPacksIndexPage = lazy(() => import("./pages/ClaimPacksIndexPage"));
const DutyRepaymentLinkingPage = lazy(() => import("./pages/DutyRepaymentLinkingPage"));
const DutyRepaymentFilingPage = lazy(() => import("./pages/DutyRepaymentFilingPage"));
const ExportDeclarationsPage = lazy(() => import("./pages/ExportDeclarationsPage"));
const ShipmentsPage = lazy(() => import("./pages/ShipmentsPage"));
const HmrcOAuthPage = lazy(() => import("./pages/HmrcOAuthPage"));

// Phase 31 — /import-review page family retired. Lazy imports for
// ImportReview*, CSVImportWizard, Opportunity*, Claim(Builder|Pack|
// Review|Approval)Page, RuleConfigPage, DQScorecardPage removed.
const FilingOutcomesPage = lazy(() => import("./pages/FilingOutcomesPage"));
const RGRReturnsPage = lazy(() => import("./pages/RGRReturnsPage"));
const WorkspaceSettingsPage = lazy(() => import("./pages/admin/WorkspaceSettingsPage"));
const HistoricalAuditCasePage = lazy(() => import("./pages/HistoricalAuditCasePage"));
const WorkflowsDashboardPage = lazy(() => import("./pages/WorkflowsDashboardPage"));
const FilingHubPage = lazy(() => import("./pages/FilingHubPage"));
const RGRCasePage = lazy(() => import("./pages/RGRCasePage"));
const CaseItemsListPage = lazy(() => import("./pages/CaseItemsListPage"));
const ReviewQueuePage = lazy(() => import("./pages/ReviewQueuePage"));
const PartnerDashboardPage = lazy(() => import("./pages/PartnerDashboardPage"));
const PartnerFilingAccessPage = lazy(() => import("./pages/PartnerFilingAccessPage"));
const PartnerClaimDetailPage = lazy(() => import("./pages/PartnerClaimDetailPage"));
const WorkspaceCreatePage = lazy(() => import("./pages/WorkspaceCreatePage"));
const WorkspaceMembersPage = lazy(() => import("./pages/WorkspaceMembersPage"));
import { UpgradeModalProvider } from "./components/UpgradeModalProvider";
import { useAuth } from "./context/AuthContext";

// Iter263 Phase C — partner_admin lands on /partner. Everyone else keeps
// their existing /home experience. Rendered inside MainLayout so the
// sidebar/header remain consistent while resolving the role.
//
// Iter263 Phase R2 — when a partner_admin has actively opened a
// merchant workspace (`activeWorkspaceId` is set to one of their
// merchant tenants), we skip the auto-redirect so `/home` renders
// the same workflow launcher tiles that the merchant sees.
function PartnerLandingRedirect() {
  const { user } = useAuth();
  const activeWs = typeof window !== 'undefined'
    ? window.localStorage.getItem('activeWorkspaceId')
    : null;
  if (user?.role === 'partner_admin' && !activeWs) {
    return <Navigate to="/partner" replace />;
  }
  if (user?.role === 'partner_viewer' && !activeWs) {
    return <Navigate to="/partner" replace />;
  }
  return <OverviewPage />;
}

// Retired /import-review audit deep links forward to the historical audit page.
function AuditReviewRedirect() {
  const { caseId } = useParams();
  return <Navigate to={`/historical-audit/${caseId}`} replace />;
}

function App() {
  return (
    <AuthProvider>
      <PermissionsProvider>
      <BrowserRouter>
        <UpgradeModalProvider>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/request-access" element={<RequestAccessPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/invite/:token" element={<SandboxInviteRedeemPage />} />
            {/* Iter263 Phase F — Workspace invite redemption (public) */}
            <Route path="/auth/invite/redeem/:token" element={<InviteRedeemPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* Trial onboarding — authenticated but no sidebar/header
                so the choice screen feels like a natural signup step. */}
            <Route path="/trial/onboarding" element={<TrialOnboardingPage />} />
            
            {/* Protected routes */}
            <Route element={<MainLayout />}>
              {/* Workflows Dashboard - Unified view with tabs */}
              <Route path="/workflows" element={<WorkflowsDashboardPage />} />
              <Route path="/filing-hub" element={<RoleGuard roles={['system_admin', 'meridian_operator', 'meridian_approver', 'client_admin', 'merchant_admin', 'merchant_operator', 'merchant_viewer', 'viewer', 'partner_admin', 'partner_viewer']}><FilingHubPage /></RoleGuard>} />
              
              {/* RGR Workflow Routes — UK */}
              <Route path="/rgr/cases/new" element={<Navigate to="/home" replace />} />
              <Route path="/rgr/cases/:id" element={<RequireModule module="uk_workflows"><RGRCasePage /></RequireModule>} />

              {/* Historical Audit Workflow Routes — UK */}
              <Route path="/historical-audit/new" element={<Navigate to="/home" replace />} />
              <Route path="/historical-audit/:id" element={<RequireModule module="uk_workflows"><HistoricalAuditCasePage /></RequireModule>} />
              
              {/* System Admin only */}
              <Route path="/admin" element={<RoleGuard roles={['system_admin']}><SystemAdminPage /></RoleGuard>} />
              <Route path="/admin/cds-platform" element={<RoleGuard roles={['system_admin']}><CDSPlatformPage /></RoleGuard>} />
              <Route path="/admin/hmrc-notifications" element={<RoleGuard roles={['system_admin', 'partner_admin', 'partner_viewer']}><HMRCNotificationsViewerPage /></RoleGuard>} />
              <Route path="/admin/settings" element={<RoleGuard roles={['system_admin', 'client_admin', 'partner_admin']}><WorkspaceSettingsPage /></RoleGuard>} />
              {/* iter226 — alias for the "natural" URL bookmarked by ops docs. */}
              <Route path="/work-queue" element={<RoleGuard roles={['meridian_operator', 'system_admin', 'partner_admin', 'partner_viewer']}><WorkQueuePage /></RoleGuard>} />
              <Route path="/approver-queue" element={<RoleGuard roles={['meridian_approver']}><ApproverQueuePage /></RoleGuard>} />
              
              {/* Broadly accessible */}
              {/* Iter236 IA — Dashboard removed from nav; keep a
                  redirect so bookmarks + inbound links don't 404. A
                  one-line "Dashboard moved to Home" toast is emitted
                  for internal users only (fires once per session via
                  DashboardPage → HomeLauncherPage; not implemented
                  as an inline toast here to keep this file tidy). */}
              <Route path="/cases" element={<CasesPage />} />
              <Route path="/cases/:id" element={<CaseDetailPage />} />
              <Route path="/approvals" element={<RoleGuard roles={['system_admin', 'meridian_approver', 'partner_admin', 'partner_viewer']}><ApprovalsQueuePage /></RoleGuard>} />
              
              {/* iter206 — Workflows now open to merchants (client_admin,
                  merchant_admin, merchant_operator, merchant_viewer),
                  brokers (broker_collaborator), and 3PL operators
                  (3pl_operator) so they can access the RGR + Import
                  Review surfaces in their own scoped workspace views. */}
              <Route path="/export-declarations" element={<RoleGuard roles={['meridian_operator', 'meridian_approver', 'system_admin', 'client_admin', 'merchant_admin', 'merchant_operator', 'broker_collaborator', 'partner_admin', 'partner_viewer']}><RequireModule module="uk_workflows"><ExportDeclarationsPage /></RequireModule></RoleGuard>} />
              <Route path="/connectors" element={<ConnectorManagementPage />} />
              <Route path="/claim-pack-builder/:builderId" element={<ClaimPackBuilderPage />} />
              {/* Iter S25 Phase 23 — canonical Claim Packs index.
                  Mode split (cross-workspace / workspace-scoped) is
                  derived from URL query params emitted by
                  lib/overviewCardPolicy.js so tiles + index agree. */}
              <Route path="/claim-packs" element={<RoleGuard roles={['system_admin','client_admin','meridian_operator','meridian_approver','merchant_admin','merchant_operator','merchant_viewer','partner_admin','partner_viewer','broker_collaborator']}><ClaimPacksIndexPage /></RoleGuard>} />
              <Route path="/sync-data" element={<RoleGuard roles={['system_admin', 'meridian_operator']}><RequireModule module="uk_workflows"><SyncDataViewerPage /></RequireModule></RoleGuard>} />
              <Route path="/sample-data" element={<RoleGuard roles={['meridian_operator', 'system_admin']}><RequireModule module="uk_workflows"><SampleDataPage /></RequireModule></RoleGuard>} />
              <Route path="/email-logs" element={<Navigate to="/audit-logs?tab=email-logs" replace />} />

              {/* Client + Operator accessible (UK intake surfaces) */}
              <Route path="/shipments" element={<RoleGuard roles={['meridian_operator', 'meridian_approver', 'system_admin', 'client_admin', 'merchant_admin', 'merchant_operator', 'broker_collaborator', 'partner_admin', 'partner_viewer']}><RequireModule module="uk_workflows"><ShipmentsPage /></RequireModule></RoleGuard>} />
              <Route path="/intake" element={<RoleGuard roles={['3pl_operator', 'meridian_operator', 'system_admin', 'client_admin', 'merchant_admin', 'merchant_operator', 'partner_admin', 'partner_viewer']}><RequireModule module="uk_workflows"><IntakePage /></RequireModule></RoleGuard>} />
              <Route path="/reports" element={<ReportsPage />} />
              <Route path="/eu-returns" element={<RoleGuard roles={['system_admin','client_admin','meridian_operator','meridian_approver','merchant_admin','merchant_operator','merchant_viewer','broker_collaborator','3pl_operator','partner_admin','partner_viewer']}><EUReturnsPage /></RoleGuard>} />
              <Route path="/eu-h7-compilation" element={<RoleGuard roles={['system_admin','meridian_operator','meridian_approver','partner_admin','partner_viewer']}><EUH7CompilationPage /></RoleGuard>} />
              <Route path="/eu-h7-reason-mapper" element={<RoleGuard roles={['system_admin','client_admin','merchant_admin','meridian_operator','meridian_approver','partner_admin','partner_viewer']}><EUH7ReasonMapperPage /></RoleGuard>} />
              {/* Iter S18 Phase 8 — USA lane page. Talks exclusively
                  to /api/usa/*; field labels come from
                  services/usa_field_mapper.py::US_PRIORITY_FIELD_ALIASES. */}
              <Route path="/usa-returns" element={<RoleGuard roles={['system_admin','client_admin','meridian_operator','meridian_approver','partner_admin','partner_viewer']}><USAReturnsPage /></RoleGuard>} />
              <Route path="/data-quality" element={<DataQualityPage />} />
              <Route path="/audit-logs" element={<AuditLogsPage />} />
              <Route path="/home" element={<PartnerLandingRedirect />} />
              <Route path="/insights" element={<InsightsPage />} />
              <Route path="/users" element={<UsersPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/settings/billing" element={<BillingSettingsPage />} />
              <Route path="/settings/billing/requests" element={<RoleGuard roles={['system_admin', 'client_admin', 'merchant_admin']}><BillingRequestsPage /></RoleGuard>} />
              <Route path="/settings/integrations" element={<RoleGuard roles={['system_admin', 'client_admin', 'merchant_admin', 'partner_admin']}><IntegrationsSettingsPage /></RoleGuard>} />
              <Route path="/admin/analytics" element={<AdminAnalyticsPage />} />
              <Route path="/admin/access-requests" element={<Navigate to="/admin/prospects?tab=access-requests" replace />} />
              <Route path="/admin/sandbox-invites" element={<Navigate to="/admin/prospects?tab=sandbox-invites" replace />} />
              <Route path="/admin/trial-requests" element={<Navigate to="/admin/prospects?tab=trial-requests" replace />} />
              <Route path="/admin/prospects" element={<RoleGuard roles={['system_admin']}><ProspectsCockpitPage /></RoleGuard>} />
              <Route path="/admin/feature-flags" element={<RoleGuard roles={['system_admin']}><FeatureFlagsPage /></RoleGuard>} />
              <Route path="/admin/partners/:partnerId/bulk-invite" element={<RoleGuard roles={['system_admin']}><BulkInvitePage /></RoleGuard>} />
              <Route path="/admin/sales/prospects" element={<Navigate to="/admin/prospects" replace />} />
              <Route path="/broker/clients/:clientId" element={<BrokerClientDetailPage />} />
              <Route path="/broker/clients/:clientId/cases/:caseId" element={<BrokerCaseDetailPage />} />
              <Route path="/hmrc-oauth" element={<RoleGuard roles={['system_admin', 'meridian_operator', 'client_admin', 'partner_admin', 'partner_viewer']}><RequireModule module="uk_workflows"><HmrcOAuthPage /></RequireModule></RoleGuard>} />
              <Route path="/settings/mfa" element={<MFASettingsPage />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              
              {/* Iter S25 Phase 30 (Fix 30.3) — Ops filing outcomes dashboard. */}
              <Route path="/filing-outcomes" element={<RoleGuard roles={['system_admin', 'meridian_operator']}><FilingOutcomesPage /></RoleGuard>} />

              {/* Phase 31 — /import-review page family retired. Preserve
                  deep-linked bookmarks: audit deep-link forwards to the
                  historical audit page with the same case id, everything
                  else falls back to the workflows dashboard. Specific
                  redirect MUST precede the catch-all so it wins. */}
              <Route path="/import-review/audit/:caseId" element={<AuditReviewRedirect />} />
              <Route path="/import-review/*" element={<Navigate to="/filing-hub" replace />} />

              {/* Duty Repayment — Internal only, UK module. Phase 31: partner_viewer
                  removed from the RoleGuard so the frontend matches the ws_auth
                  decorator on the filing endpoint (which does not include partner_viewer). */}
              <Route path="/duty-repayment/:caseId/linking" element={<RoleGuard roles={['system_admin', 'meridian_operator', 'meridian_approver', 'partner_admin']}><RequireModule module="uk_workflows"><DutyRepaymentLinkingPage /></RequireModule></RoleGuard>} />
              <Route path="/duty-repayment/:caseId/filing" element={<RoleGuard roles={['system_admin', 'meridian_operator', 'meridian_approver', 'partner_admin']}><RequireModule module="uk_workflows"><DutyRepaymentFilingPage /></RequireModule></RoleGuard>} />

              {/* RGR Returns — UK module */}
              <Route path="/rgr-returns" element={<RequireModule module="uk_workflows"><RGRReturnsPage /></RequireModule>} />

              {/* Item-Level Spine (iter201) — case_items + auto-link review queue */}
              <Route path="/case-items" element={<RoleGuard roles={['system_admin', 'meridian_operator', 'meridian_approver', 'client_admin', 'merchant_admin', 'merchant_operator', 'merchant_viewer', 'partner_admin', 'partner_viewer']}><CaseItemsListPage /></RoleGuard>} />
              <Route path="/review-queue" element={<RoleGuard roles={['system_admin', 'meridian_operator', 'meridian_approver', 'client_admin', 'merchant_admin', 'merchant_operator', 'partner_admin', 'partner_viewer']}><ReviewQueuePage /></RoleGuard>} />
              {/* USA lane placeholder (iter204) — Iter S25 Phase 27.5 (Fix 3): route retired. */}

              {/* Iter263 Phase C — Partner Dashboard (partner_admin landing) */}
              <Route path="/partner" element={<RoleGuard roles={['partner_admin', 'partner_viewer', 'system_admin']}><PartnerDashboardPage /></RoleGuard>} />
              <Route path="/partner/filing-access" element={<RoleGuard roles={['partner_admin', 'system_admin']}><PartnerFilingAccessPage /></RoleGuard>} />
              {/* Iter263 Phase U3 — read-only merchant claim detail */}
              <Route path="/partner/claims/:claimId" element={<RoleGuard roles={['partner_admin', 'partner_viewer']}><PartnerClaimDetailPage /></RoleGuard>} />
              <Route path="/partner/returns" element={<RoleGuard roles={['partner_admin', 'partner_viewer']}><CasesPage /></RoleGuard>} />
              {/* Iter263 Phase D — Workspace provisioning + invites */}
              <Route path="/partner/workspaces/new" element={<RoleGuard roles={['partner_admin', 'system_admin']}><WorkspaceCreatePage /></RoleGuard>} />
              {/* Iter263 Phase E — Workspace Settings Members tab */}
              <Route path="/partner/workspaces/:workspaceId/members" element={<RoleGuard roles={['partner_admin', 'partner_viewer', 'system_admin']}><WorkspaceMembersPage /></RoleGuard>} />
              {/* Iter263 Phase M2 — Merchant workspace detail */}
              <Route path="/partner/workspaces/:workspaceId" element={<RoleGuard roles={['partner_admin', 'partner_viewer', 'system_admin']}><WorkspaceMembersPage /></RoleGuard>} />
            </Route>
            
            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/home" replace />} />
            <Route path="*" element={<Navigate to="/home" replace />} />
          </Routes>
        </Suspense>
        </UpgradeModalProvider>
      </BrowserRouter>
      <Toaster />
      </PermissionsProvider>
    </AuthProvider>
  );
}

export default App;
